import { useCallback, useRef, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { workosAuthService, type WorkOSUser } from 'services/workos-auth.service'
import { renewPlatformSession } from 'services/platformSession'
import { isInternalRoleName } from 'constants/roles'
import { ROUTES, shouldEndSpaceOnLoad } from 'routes'
import { endVeraSpaceSession } from 'stores'
import { storage, API_BASE_URL, isLocalApiUrl } from 'utils'
import {
  INITIAL_DEV_SURFACE,
  hasActiveVeraSpace,
  isInternalPath,
  mintDevSession,
  type DevSurface,
} from 'utils/devSurface'

interface UseWorkOSAuthReturn {
  user: WorkOSUser | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  login: (domain?: string) => Promise<void>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
}

export const WORKOS_PROFILE_QUERY_KEY = ['workos', 'profile']

// Track if initial auth check has been performed
let isAuthChecked = false

// Dev-session: which surface the current dev token was minted for, so navigating
// across the company<->internal boundary re-establishes as the surface-appropriate
// account (working-conventions §17: /internal/* → platform_admin, else company_admin).
let devSessionSurface: DevSurface | null = null

// VRG-564: this derivation used to be hand-rolled HERE and again in `services/index.ts`
// (the second copy annotated "Must match useWorkOSAuth's DEV_DEFAULT_SURFACE" — one rule,
// two hand-maintained copies, §36). It now lives in exactly one place, `utils/devSurface`,
// which is also where the VERA-876 veraSpace carve-out and the reason
// VITE_DEV_DEFAULT_SURFACE no longer speaks for named company routes are documented.
//
// Captured at module load (page load) BEFORE React Router can redirect — so a cold load of
// /internal/* mints the internal account even though the guard bounces the URL to a company
// page before the async mint completes. The URL you load decides the account; switching
// surface = open the other URL (or reload).
const INITIAL_SURFACE: DevSurface = INITIAL_DEV_SURFACE

// The internal URL the page was cold-loaded on, captured before React Router can redirect.
// The InternalRoute guard bounces to /certificates during the brief window where auth is
// settling (dev-session re-mint in flight → user momentarily not-yet-internal), and never
// restores the URL. We stash the intended path here and, once the internal admin is confirmed,
// navigate back to it exactly once — so a cold load of /internal/* actually LANDS there.
// Uses the shared `isInternalPath` rather than a bare `startsWith`, so this and the surface
// derivation can never disagree about what counts as an internal route (VRG-564). It also
// stops `/internalise` being stashed as an internal path to restore.
const INITIAL_INTERNAL_PATH =
  typeof window !== 'undefined' && isInternalPath(window.location.pathname)
    ? window.location.pathname + window.location.search
    : null
let internalPathRestored = false

// VRG-661: a cold load of /internal/* is an explicit request to act as Vera staff, so it
// ends any company space persisted from an earlier session BEFORE the router guards run.
// Without this, InternalRoute's gate 1 treats the staffer as a brand user and bounces the
// URL to that brand's Settings, and the restore effect below cannot win (it navigates back,
// the guard bounces again). Runs at module load for the same reason INITIAL_INTERNAL_PATH
// is captured here: it has to precede the first render. Entering a space from an internal
// page happens in-app, after load, so it is untouched. See `shouldEndSpaceOnLoad`.
if (
  typeof window !== 'undefined' &&
  shouldEndSpaceOnLoad({
    pathname: window.location.pathname,
    hasActiveSpace: hasActiveVeraSpace(),
    isInternalPath,
  })
) {
  endVeraSpaceSession()
}

// ---------------------------------------------------------------------------
// Dev auth bypass — set VITE_SKIP_AUTH=true in apps/platform/.env.local to
// skip WorkOS authentication entirely. Never enabled in production.
//
// Real dev JWT — set VITE_DEV_SESSION=true and VITE_DEV_SESSION_SECRET (must match
// API DEV_SESSION_SECRET). Calls POST /auth/dev/session when no accessToken is set.
// If both SKIP_AUTH and DEV_SESSION are enabled, DEV_SESSION takes priority.
// ---------------------------------------------------------------------------
const SKIP_AUTH = import.meta.env.VITE_SKIP_AUTH === 'true'
const DEV_SESSION = import.meta.env.VITE_DEV_SESSION === 'true'

// Loopback OR a private LAN address counts as local — see isLocalApiUrl. Testing from
// a real phone needs the machine's LAN IP (a handset cannot reach the Mac's localhost),
// and a loopback-only check disabled the bypass precisely when device testing needed it.
// Deployed hosts are still rejected, which is what these guards are actually for.
const API_IS_LOCAL = isLocalApiUrl(import.meta.env.VITE_REACT_APP_API_URL)

if (SKIP_AUTH && !API_IS_LOCAL) {
  throw new Error(
    'VITE_SKIP_AUTH=true is set but the API URL is not a local address ' +
    '(localhost/127.0.0.1 or a private LAN IP). ' +
    'Remove VITE_SKIP_AUTH from your environment before building against a non-local API.'
  )
}

if (DEV_SESSION && !API_IS_LOCAL) {
  throw new Error(
    'VITE_DEV_SESSION=true cannot be used unless VITE_REACT_APP_API_URL points at a ' +
    'local address (localhost/127.0.0.1 or a private LAN IP).'
  )
}

// Dev-session surface race fix: if the token already in storage was minted for a
// DIFFERENT surface than the URL now being loaded (e.g. a company token left over
// after a guard bounce, while loading an /internal/* page), CLEAR it here — at module
// load, before React renders. Otherwise `isAuthenticated()` is true on that stale
// token, the page renders immediately, and its data queries fire with the wrong-surface
// token (internal endpoints 401) before checkAuth's async re-mint completes. Clearing it
// makes the app start unauthenticated so checkAuth mints the correct surface FIRST.
if (typeof window !== 'undefined' && DEV_SESSION) {
  try {
    const held = storage.getItem('accessToken')
    if (held) {
      const payload = JSON.parse(
        atob(String(held).split('.')[1].replace(/-/g, '+').replace(/_/g, '/')),
      )
      const heldSurface = String(payload?.roleName ?? '').startsWith('platform_')
        ? 'internal'
        : 'company'
      if (heldSurface !== INITIAL_SURFACE) {
        storage.removeItem('accessToken')
        storage.removeItem('refreshToken')
      }
    }
  } catch {
    // Unparseable token — leave it; checkAuth re-establishes as needed.
  }
}

const DEV_BYPASS_USER: WorkOSUser = {
  id: 'dev-bypass-user',
  email: 'dev-bypass@vera.local',
  firstName: 'Dev',
  lastName: 'Bypass',
  fullName: 'Dev Bypass',
  role: {
    name: 'admin',
    permissions: [],
  },
  company: {
    id: 'dev-company',
    name: 'Local Testing Company',
    workosOrgId: null,
  },
}

export function useWorkOSAuth(): UseWorkOSAuthReturn {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isCheckingAuth = useRef(false)

  // Use React Query for user profile data
  const {
    data: user,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: WORKOS_PROFILE_QUERY_KEY,
    queryFn: async () => {
      if (SKIP_AUTH && !DEV_SESSION) return DEV_BYPASS_USER
      if (!workosAuthService.isAuthenticated()) {
        return null
      }
      return await workosAuthService.getProfile()
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: (SKIP_AUTH && !DEV_SESSION) || workosAuthService.isAuthenticated(),
  })

  const isAuthenticated =
    SKIP_AUTH && !DEV_SESSION ? !!user : !!user && workosAuthService.isAuthenticated()
  const error = queryError ? 'Authentication check failed' : null

  // Restore the internal URL the guard bounced away from during the auth-settle window (once
  // the internal admin is confirmed). One-shot per page load. Only ever restores an /internal/*
  // URL the page was actually loaded on, so it can't hijack a later intentional navigation.
  useEffect(() => {
    if (internalPathRestored || !INITIAL_INTERNAL_PATH || isLoading) return
    // VRG-410 — the shared predicate. A `vera_fulfillment` staffer deep-linking to
    // /internal/orders was bounced by the guard during the auth-settle window and then
    // never restored, because this copy of the list did not know they were internal.
    if (!isInternalRoleName((user as WorkOSUser | null)?.role?.name)) return
    internalPathRestored = true
    const current = window.location.pathname + window.location.search
    if (current !== INITIAL_INTERNAL_PATH) {
      navigate(INITIAL_INTERNAL_PATH, { replace: true })
    }
  }, [user, isLoading, navigate])

  /**
   * Initiate login flow
   */
  const login = useCallback(async (domain?: string) => {
    // Note: This will redirect, so loading state doesn't matter much
    try {
      const loginUrl = await workosAuthService.getLoginUrl(domain)
      window.location.href = loginUrl
    } catch (err) {
      console.error('Login error:', err)
    }
  }, [])

  /**
   * Logout user
   */
  const logout = useCallback(async () => {
    try {
      await workosAuthService.logout()
      isAuthChecked = false
      // Only suppress auto-login OUTSIDE dev. In dev (VITE_DEV_SESSION) we want to
      // always be logged in — suppressing here (and never clearing it) is what left
      // the app stuck on /sign-in after any logout / transient 401.
      if (!DEV_SESSION) sessionStorage.setItem('dev_session_suppressed', '1')
      // Clear React Query cache
      queryClient.setQueryData(WORKOS_PROFILE_QUERY_KEY, null)
      queryClient.invalidateQueries({ queryKey: WORKOS_PROFILE_QUERY_KEY })
      navigate(ROUTES.SIGN_IN)
    } catch (err) {
      console.error('Logout error:', err)
    }
  }, [navigate, queryClient])

  /**
   * Check authentication status and load user
   */
  const checkAuth = useCallback(async () => {
    // Prevent multiple simultaneous auth checks
    if (isCheckingAuth.current) {
      return
    }

    try {
      isCheckingAuth.current = true

      // In dev, always (re)establish the session — ignore any stale suppression flag
      // so the app recovers automatically after a logout / transient 401 instead of
      // getting stuck on /sign-in. (To intentionally test the real sign-in flow,
      // disable VITE_DEV_SESSION.)
      // Re-mint when there's no token OR the held token is for the wrong surface
      // (e.g. a hard reload on /internal/* while holding a company token) so the
      // account always matches the surface (§17). devSessionSurface resets to null
      // on a full page load, so the first check after a reload always re-mints with
      // the route's surface.
      const devSurface = INITIAL_SURFACE
      if (DEV_SESSION && (!workosAuthService.isAuthenticated() || devSessionSurface !== devSurface)) {
        const apiUrl = API_BASE_URL
        const secret = import.meta.env.VITE_DEV_SESSION_SECRET
        if (!secret) {
          console.error(
            'VITE_DEV_SESSION is enabled but VITE_DEV_SESSION_SECRET is missing. Set it in apps/platform/.env.local to match DEV_SESSION_SECRET on the API.'
          )
          isAuthChecked = true
          queryClient.setQueryData(WORKOS_PROFILE_QUERY_KEY, null)
          return
        }
        try {
          // Shared mint (VRG-564) — collapses this onto the same in-flight request as any
          // 401 re-mint racing it, so a cold load stops making three round-trips and three
          // cookie writes for one answer.
          const token = await mintDevSession({
            apiBaseUrl: apiUrl,
            secret,
            surface: devSurface,
          })
          if (!token) {
            throw new Error('Dev session response did not include a token')
          }
          storage.setItem('accessToken', token)
          storage.removeItem('refreshToken')
          devSessionSurface = devSurface
        } catch (err) {
          console.error('Failed to obtain dev session token:', err)
          isAuthChecked = true
          queryClient.setQueryData(WORKOS_PROFILE_QUERY_KEY, null)
          return
        }
      }

      // Dev bypass — skip WorkOS entirely (DEV_SESSION wins if both flags are set)
      if (SKIP_AUTH && !DEV_SESSION) {
        queryClient.setQueryData(WORKOS_PROFILE_QUERY_KEY, DEV_BYPASS_USER)
        isAuthChecked = true
        return
      }

      // Handle callback if code present in URL
      const urlParams = new URLSearchParams(location.search)
      if (urlParams.has('code')) {
        const result = await workosAuthService.handleLoginRedirect(urlParams)
        if (result) {
          isAuthChecked = true
          // Update React Query cache with the new user
          queryClient.setQueryData(WORKOS_PROFILE_QUERY_KEY, result.user)
          // Redirect based on user role. VRG-410 — `isInternalRoleName`, not a sixth
          // inline copy of the pair: `vera_fulfillment` and `vera_finance` were being
          // sent to /certificates, a BRAND page they have no company for, on every
          // sign-in. All four internal roles hold `view_companies`, so the internal
          // landing opens for each of them.
          //
          // VRG-661: internal users go to `/`, the one post-auth decision point
          // (`resolveHomeLanding`), instead of a hardcoded INTERNAL_COMPANIES here, so this
          // path cannot drift from the others. Company users keep /certificates, unchanged.
          const isInternalUser = isInternalRoleName(result.user.role?.name)
          navigate(isInternalUser ? ROUTES.HOME : ROUTES.CERTIFICATES, { replace: true })
          return
        }
      }

      // No access token in this tab (a fresh browser start clears the session-scoped token
      // cookie; or it was never set here) — but the HttpOnly session cookie may still hold a
      // live platform session. Renew silently from it: this is what keeps someone signed in
      // across browser restarts for up to 7 idle / 30 total days. Fails quietly (null) when
      // there is no session, and the app shows sign-in as before.
      if (!DEV_SESSION && !SKIP_AUTH && !workosAuthService.isAuthenticated()) {
        await renewPlatformSession()
      }

      // Check existing authentication
      if (workosAuthService.isAuthenticated()) {
        const profile = await workosAuthService.getProfile()
        if (profile) {
          isAuthChecked = true
          queryClient.setQueryData(WORKOS_PROFILE_QUERY_KEY, profile)
        } else {
          // Token invalid, clear it
          await workosAuthService.logout()
          isAuthChecked = true
          queryClient.setQueryData(WORKOS_PROFILE_QUERY_KEY, null)
        }
      } else {
        isAuthChecked = true
      }
    } catch (err) {
      console.error('Auth check error:', err)
      isAuthChecked = true
      queryClient.setQueryData(WORKOS_PROFILE_QUERY_KEY, null)
    } finally {
      isCheckingAuth.current = false
    }
  }, [navigate, queryClient, location.search])

  // Check auth ONLY on mount or when there's an OAuth callback code in the URL
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const hasCode = urlParams.has('code')

    // Only run checkAuth if:
    // 1. Auth hasn't been checked yet (initial mount)
    // 2. There's an OAuth callback code in the URL
    if (!isAuthChecked || hasCode) {
      checkAuth()
    }
  }, [location.search, checkAuth])


  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    checkAuth,
  }
}